import { useEffect, useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getCustomer,
  patchCustomer,
  putCustomerDiscounts,
  type CustomerDetail,
} from "../../lib/api.js";

/**
 * Detail klienta — info, adresa, slevy. Editovatelné inline.
 */
export function CustomerDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    getCustomer(id)
      .then((c) => mounted && setData(c))
      .catch((e: unknown) => mounted && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      mounted = false;
    };
  }, [id]);

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Chyba: {error}
        </div>
        <Link to="/admin/customers" className="mt-4 inline-block text-sm text-ktm-orange">
          ← Zpět na seznam
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 text-sm text-zinc-500">Načítám…</div>
    );
  }

  const personName = `${data.lastName ?? ""} ${data.firstName ?? ""}`.trim();
  const title = data.companyName ?? (personName || data.email);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/admin/customers"
        className="text-sm text-zinc-500 hover:text-ktm-orange inline-flex items-center gap-1 mb-4"
      >
        ← Zpět na seznam
      </Link>

      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-semibold text-zinc-900">{title}</h1>
            {data.isB2B ? (
              <span className="inline-block text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-violet-100 text-violet-700">
                B2B
              </span>
            ) : (
              <span className="inline-block text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-100 text-zinc-600">
                B2C
              </span>
            )}
          </div>
          <div className="text-sm text-zinc-500">
            ID: <span className="font-mono">{data.legacyId ?? "—"}</span> · Registrace:{" "}
            {formatDate(data.registeredAt)}
          </div>
        </div>
        {data.shopifyCustomerId ? (
          <span className="inline-block text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-emerald-100 text-emerald-700">
            ✓ Shopify · {data.shopifyCustomerId}
          </span>
        ) : (
          <span className="inline-block text-xs font-medium px-2.5 py-1 rounded bg-zinc-100 text-zinc-500">
            Není v Shopify
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CustomerInfoForm data={data} onUpdated={setData} />
        <DiscountsEditor data={data} onUpdated={setData} />
      </div>

      {data.rawAddress && (
        <div className="mt-6 rounded-lg bg-zinc-50 px-4 py-3 text-xs text-zinc-500">
          <span className="font-semibold uppercase tracking-wider mr-2">
            Původní adresa:
          </span>
          <span className="font-mono">{data.rawAddress}</span>
        </div>
      )}

      <div className="mt-6 text-xs text-zinc-400">
        Importováno z {data.importSource ?? "—"} ·{" "}
        {data.importedAt ? formatDate(data.importedAt) : "—"} · Poslední úprava{" "}
        {formatDate(data.updatedAt)}
      </div>
    </div>
  );
}

// ─── Info form ──────────────────────────────────────────────────────────

function CustomerInfoForm({
  data,
  onUpdated,
}: {
  data: CustomerDetail;
  onUpdated: (c: CustomerDetail) => void;
}): JSX.Element {
  const [form, setForm] = useState({
    firstName: data.firstName ?? "",
    lastName: data.lastName ?? "",
    email: data.email,
    phone: data.phone ?? "",
    ico: data.ico ?? "",
    dic: data.dic ?? "",
    companyName: data.companyName ?? "",
    street: data.street ?? "",
    city: data.city ?? "",
    zip: data.zip ?? "",
    country: data.country ?? "CZ",
    notes: data.notes ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const payload: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(form)) {
        const val = v.trim();
        payload[k] = val.length === 0 ? null : val;
      }
      const res = await patchCustomer(data.id, payload as never);
      onUpdated({ ...data, ...res.customer, isB2B: !!res.customer.companyName });
      setMsg("Uloženo ✓");
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-zinc-100">
        <h2 className="font-semibold text-zinc-900">Kontaktní údaje</h2>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Jméno" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} />
          <Field label="Příjmení" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
        </div>
        <Field label="E-mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Firma" value={form.companyName} onChange={(v) => setForm({ ...form, companyName: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="IČO" value={form.ico} onChange={(v) => setForm({ ...form, ico: v })} />
          <Field label="DIČ" value={form.dic} onChange={(v) => setForm({ ...form, dic: v })} />
        </div>
        <Field label="Ulice" value={form.street} onChange={(v) => setForm({ ...form, street: v })} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Město" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          </div>
          <Field label="PSČ" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
        </div>
        <Field label="Země" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-1">
            Poznámky
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ktm-orange focus:border-transparent"
          />
        </div>
      </div>
      <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
        {msg && (
          <span
            className={`text-xs ${
              msg.includes("✓") ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {msg}
          </span>
        )}
        <button
          type="submit"
          disabled={saving}
          className="ml-auto bg-ktm-orange hover:bg-orange-600 disabled:bg-zinc-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
        >
          {saving ? "Ukládám…" : "Uložit"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ktm-orange focus:border-transparent"
      />
    </div>
  );
}

// ─── Discounts editor ───────────────────────────────────────────────────

function DiscountsEditor({
  data,
  onUpdated,
}: {
  data: CustomerDetail;
  onUpdated: (c: CustomerDetail) => void;
}): JSX.Element {
  const [items, setItems] = useState(
    data.discounts.map((d) => ({ category: d.category, percent: d.percent })),
  );
  const [newCat, setNewCat] = useState("");
  const [newPct, setNewPct] = useState<number>(10);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function addRow(): void {
    if (!newCat.trim()) return;
    setItems([...items, { category: newCat.trim(), percent: newPct }]);
    setNewCat("");
    setNewPct(10);
  }

  function removeRow(idx: number): void {
    setItems(items.filter((_, i) => i !== idx));
  }

  function updateRow(idx: number, field: "category" | "percent", value: string): void {
    setItems(
      items.map((it, i) =>
        i === idx
          ? {
              ...it,
              [field]: field === "percent" ? Number(value) || 0 : value,
            }
          : it,
      ),
    );
  }

  async function save(): Promise<void> {
    setSaving(true);
    setMsg(null);
    try {
      const payload = items
        .filter((it) => it.category.trim().length > 0)
        .map((it) => ({
          category: it.category.trim(),
          percent: Math.max(0, Math.min(100, Math.round(it.percent))),
        }));
      await putCustomerDiscounts(data.id, payload);
      onUpdated({
        ...data,
        discounts: payload.map((p, i) => ({ id: `tmp-${i}`, ...p })),
      });
      setMsg("Uloženo ✓");
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  }

  return (
    <div className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-100">
        <h2 className="font-semibold text-zinc-900">Slevy na kategorie</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          B2B sazby — aplikuje se nejvyšší shoda kategorie produktu.
        </p>
      </div>
      <div className="p-5 space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-zinc-400 py-3">Žádné slevy.</div>
        )}
        {items.map((it, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 group"
          >
            <input
              type="text"
              value={it.category}
              onChange={(e) => updateRow(idx, "category", e.target.value)}
              className="flex-1 px-2.5 py-1.5 border border-zinc-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-ktm-orange"
              placeholder="Kategorie"
            />
            <div className="relative w-20">
              <input
                type="number"
                min="0"
                max="100"
                value={it.percent}
                onChange={(e) => updateRow(idx, "percent", e.target.value)}
                className="w-full px-2.5 py-1.5 pr-6 border border-zinc-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-ktm-orange"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400">
                %
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="text-zinc-300 hover:text-red-500 transition-colors"
              aria-label="Smazat"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1.5 14a2 2 0 0 1-2 1.83H8.5A2 2 0 0 1 6.5 20L5 6m5 0V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        ))}

        <div className="pt-3 border-t border-zinc-100 flex items-center gap-2">
          <input
            type="text"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRow();
              }
            }}
            placeholder="Nová kategorie…"
            className="flex-1 px-2.5 py-1.5 border border-zinc-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-ktm-orange"
          />
          <div className="relative w-20">
            <input
              type="number"
              min="0"
              max="100"
              value={newPct}
              onChange={(e) => setNewPct(Number(e.target.value) || 0)}
              className="w-full px-2.5 py-1.5 pr-6 border border-zinc-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-ktm-orange"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400">
              %
            </span>
          </div>
          <button
            type="button"
            onClick={addRow}
            className="px-3 py-1.5 text-sm text-zinc-600 border border-zinc-300 rounded hover:bg-zinc-50"
          >
            + Přidat
          </button>
        </div>
      </div>
      <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
        {msg && (
          <span
            className={`text-xs ${
              msg.includes("✓") ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {msg}
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="ml-auto bg-ktm-orange hover:bg-orange-600 disabled:bg-zinc-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
        >
          {saving ? "Ukládám…" : "Uložit slevy"}
        </button>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
